import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { AuthUser } from "../@types/auth-user";
import { AdminUser } from "../@types/admin-user";
import { TicketOffice } from "../@types/ticket-office";
import { useAdminUserDetails } from "../services/api-tansquery/admin-user-details";
import { useTicketOfficeList } from "../services/api-tansquery/ticket-office-list";
import { useAuthUserDetails } from "../services/api-tansquery/auth-user-details";
import { loginRedirect } from "../services/auth";

type AuthContextType = {
    authUser: null | AuthUser;
    isAuthUserLoading: boolean;
    adminUser: null | AdminUser;
    isAdminUserLoading: boolean;
    ticketOfficeList: Array<TicketOffice>;
    isTicketOfficeListLoading: boolean;
    currentTicketOffice: TicketOffice | null;
    changeCurrentTicketOffice: (ticketOffice: TicketOffice | null) => void;
};

const AuthContext = createContext<AuthContextType>({
    authUser: null,
    isAuthUserLoading: true,
    adminUser: null,
    isAdminUserLoading: true,
    ticketOfficeList: [],
    isTicketOfficeListLoading: true,
    currentTicketOffice: null,
    changeCurrentTicketOffice: () => { },
});

export default function AuthProvider({ children }: { children: React.ReactNode }) {
    const { data: authUser, isLoading: isAuthUserLoading, error } = useAuthUserDetails();
    const { data: adminUser, isLoading: isAdminUserLoading } = useAdminUserDetails();
    const { data: ticketOfficeList, isLoading: isTicketOfficeListLoading } = useTicketOfficeList();
    const [currentTicketOffice, setCurrentTicketOffice] = useState<TicketOffice>();

    function changeCurrentTicketOffice(ticketOffice: TicketOffice | null) {
        setCurrentTicketOffice(ticketOffice);

        if (ticketOffice) {
            localStorage.setItem("gov_ticket_office", JSON.stringify(ticketOffice.id));
        } else {
            localStorage.removeItem("gov_ticket_office");
        }
    }

    const loadCurrentTicketOffice = useCallback(() => {
        const storedTicketOffice = localStorage.getItem("gov_ticket_office");

        if (storedTicketOffice && !isTicketOfficeListLoading) {
            const ticketOfficeId = Number(JSON.parse(storedTicketOffice));
            const ticketOffice = ticketOfficeList?.find(to => to.id === ticketOfficeId) || null;

            if (!ticketOffice) {
                localStorage.removeItem("gov_ticket_office");
            }

            setCurrentTicketOffice(ticketOffice);
        }
    }, [ticketOfficeList, isTicketOfficeListLoading]);

    const handleAuthError = useCallback(() => {
        if (error?.response?.data?.detail.includes("Token inválido")) {
            console.error("Error loading auth data:", error);
            loginRedirect(window.location.pathname);
        }
    }, [error]);

    useEffect(handleAuthError, [handleAuthError]);
    useEffect(loadCurrentTicketOffice, [loadCurrentTicketOffice]);

    return (
        <AuthContext.Provider value={{
            authUser,
            isAuthUserLoading,
            adminUser,
            isAdminUserLoading,
            ticketOfficeList,
            isTicketOfficeListLoading,
            currentTicketOffice,
            changeCurrentTicketOffice
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);

    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }

    return context;
}
